import React, { useEffect, useRef, useState } from "react";
import {
  Text,
  Panel,
  Icon,
  theme,
  useResponsive,
  UserStylesProps,
} from "@clearabee/ui-library";
import { icons, Icons } from "@clearabee/ui-library-base";
import { styles } from "./Sidebar.styles";
import { Anchor } from "components/Core/Anchor/Anchor";
import { Redirect } from "lib/routes";

type TabProps = UserStylesProps & {
  tab: string;
  title: string;
  icon?: keyof Icons;
  isDisabled?: boolean;
} & (
    | {
        onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
        redirect?: undefined;
      }
    | {
        onClick?: undefined;
        redirect: Redirect;
      }
  );

interface SidebarProps extends UserStylesProps {
  tabs: TabProps[];
  initialTab?: string;
}

interface ScrollItem {
  hasMore: boolean;
  position: number;
}

interface ScrollProps extends Partial<ScrollItem> {
  transform?: string;
  itemsElement: HTMLElement | null;
}

interface ScrollItems {
  next?: ScrollItem;
  previous?: ScrollItem;
}

interface WrapperProps {
  children: React.ReactNode;
}

const Scroll = ({
  position,
  hasMore,
  transform,
  itemsElement,
}: ScrollProps): React.ReactElement => {
  return (
    <div
      css={[styles.chevron, !hasMore && styles.chevronDisabled]}
      {...(hasMore
        ? {
            onClick: () => {
              itemsElement?.scrollTo({
                left: position,
                behavior: "smooth",
              });
            },
          }
        : {})}
    >
      <Icon.Chevron
        color={hasMore ? "brand.light" : "brand.darkest"}
        transform={transform}
        size="small"
      />
    </div>
  );
};

const getScrollItems = (
  ref: HTMLDivElement | null,
  isActive: (index: number) => boolean,
  initial = false,
): ScrollItems | undefined => {
  if (!ref) {
    return;
  }

  const htmlItems = Array.from(ref.children) as HTMLElement[];
  const offset = ref.offsetLeft;
  const newScrollItems: ScrollItems = {};
  const { scrollLeft, scrollWidth, clientWidth } = ref;
  const scrollRight = scrollLeft + clientWidth;

  if (scrollWidth <= clientWidth) {
    return {};
  }

  htmlItems.forEach((item, index) => {
    const itemLeft = item.offsetLeft - offset;
    const itemRight = itemLeft + item.clientWidth;

    if (!itemLeft || itemLeft < scrollLeft) {
      newScrollItems.previous = {
        position: itemLeft,
        hasMore: scrollLeft !== 0,
      };
    } else if (itemRight === scrollWidth || itemRight > scrollRight) {
      if (!newScrollItems.next) {
        newScrollItems.next = {
          position: itemRight - ref.offsetWidth,
          hasMore: scrollRight < scrollWidth,
        };
      }
      if (initial && isActive(index)) {
        ref.scrollTo({ left: itemRight - ref.offsetWidth });
      }
    }
  });

  if (!newScrollItems.next) {
    return {};
  }

  return newScrollItems;
};

export const Sidebar = ({
  tabs,
  initialTab,
  styles: userStyles,
}: SidebarProps): React.ReactElement => {
  const [scrollItems, setScrollItems] = useState<ScrollItems>();
  const { screens } = useResponsive();
  const itemsRef = useRef<HTMLDivElement>(null);
  const activeItem = tabs.find(({ tab }) => tab === initialTab);
  const isVertical = screens.large;

  const isActive = (index: number) => tabs[index].tab === activeItem?.tab;

  useEffect(() => {
    const listener = () => {
      setScrollItems(getScrollItems(itemsRef.current, isActive));
    };

    itemsRef.current?.addEventListener("scroll", listener);
    window.addEventListener("resize", listener);
    setScrollItems(getScrollItems(itemsRef.current, isActive, true));

    return () => {
      itemsRef.current?.removeEventListener("scroll", listener);
      window.removeEventListener("resize", listener);
    };
  }, []);

  return (
    <Panel
      shadow={false}
      styles={[
        isVertical ? styles.verticalPanel : styles.horizontalPanel,
        userStyles,
      ]}
    >
      {!isVertical && (
        <Scroll
          {...scrollItems?.previous}
          itemsElement={itemsRef.current}
          transform="scale(-1, 1)"
        />
      )}
      <div
        css={isVertical ? styles.verticalContainer : styles.horizontalContainer}
        ref={itemsRef}
      >
        {tabs.map(
          (
            {
              title,
              tab,
              icon,
              onClick,
              isDisabled,
              styles: itemStyles,
              redirect,
            },
            index,
          ) => {
            const ConditionalAnchor = ({ children }: WrapperProps) =>
              redirect ? (
                <Anchor {...redirect}>{children}</Anchor>
              ) : (
                <>{children}</>
              );

            return (
              <ConditionalAnchor key={`${tab}-${index}`}>
                <div
                  onClick={onClick}
                  css={[
                    itemStyles,
                    styles.item,
                    !isDisabled && {
                      cursor: "pointer",
                      ":hover": {
                        backgroundColor: `${theme.colors.brand.darker}55`,
                      },
                    },
                    !isVertical && {
                      paddingLeft: theme.spacing.xsmall,
                      paddingRight: theme.spacing.xsmall,
                    },
                  ]}
                >
                  {isVertical && (
                    <div
                      style={{
                        width: theme.spacing.xsmall2,
                        height: "60%",
                        backgroundColor: isActive(index)
                          ? theme.colors.brand.base
                          : undefined,
                        marginRight: theme.spacing.large,
                      }}
                    />
                  )}
                  {icon && (
                    <Icon
                      width={20}
                      height={20}
                      {...icons[icon]}
                      color={
                        !isVertical && isActive(index) ? "brand.light" : "light"
                      }
                    />
                  )}
                  <Text
                    fontSize="small"
                    styles={{
                      paddingLeft: theme.spacing.small,
                      fontWeight: isActive(index) ? 600 : 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {title}
                  </Text>
                </div>
              </ConditionalAnchor>
            );
          },
        )}
      </div>
      {!isVertical && (
        <Scroll itemsElement={itemsRef.current} {...scrollItems?.next} />
      )}
    </Panel>
  );
};
